export interface ApiErrorEnvelope {
  readonly error?: {
    readonly code?: string;
    readonly message?: string;
    readonly details?: unknown;
  };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export function apiUrl(): string {
  const raw = import.meta.env.VITE_API_URL ?? "";
  return raw.replace(/\/+$/, "");
}

export function apiUrlLabel(): string {
  return apiUrl() === "" ? "same origin" : apiUrl();
}

function apiPath(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${apiUrl()}/api/v1${normalized}`;
}

export function apiHref(path: string): string {
  return apiPath(path);
}

function redirectToLogin(): void {
  if (window.location.pathname === "/login") return;
  window.history.replaceState(null, "", "/login");
  window.dispatchEvent(new PopStateEvent("popstate"));
}

async function responseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const type = response.headers.get("content-type") ?? "";
  if (type.includes("application/json")) {
    try {
      return (await response.json()) as unknown;
    } catch {
      return undefined;
    }
  }
  return await response.text();
}

function errorEnvelope(value: unknown): ApiErrorEnvelope["error"] {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return undefined;
  const error = (value as ApiErrorEnvelope).error;
  return typeof error === "object" && error !== null ? error : undefined;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  headers.set("accept", "application/json, text/plain;q=0.9");
  const response = await fetch(apiPath(path), {
    ...init,
    headers,
    credentials: "same-origin",
  });
  const body = await responseBody(response);
  if (response.ok) return body as T;
  const envelope = errorEnvelope(body);
  const message = envelope?.message ?? `Request failed with HTTP ${String(response.status)}`;
  const error = new ApiError(
    response.status,
    envelope?.code ?? "request_failed",
    message,
    envelope?.details,
  );
  if (response.status === 401) redirectToLogin();
  throw error;
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value);
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "The request could not be completed.";
}

/** One structured validation issue from the backend's `config_invalid` envelope (J10). */
export interface ConfigFieldIssue {
  readonly path: string;
  readonly message: string;
}

/**
 * Parses the REAL ConfigError envelope into a per-field map. The backend's
 * `details` IS the issues array (rest/errors.ts): `{ error: { code:
 * "config_invalid", details: [{ path, code, message }] } }` — there is no
 * intermediate `issues` wrapper.
 */
export function configFieldErrors(error: unknown): Readonly<Record<string, string>> {
  if (!(error instanceof ApiError)) return {};
  if (!Array.isArray(error.details)) return {};
  const map: Record<string, string> = {};
  for (const issue of error.details) {
    if (typeof issue !== "object" || issue === null) continue;
    const record = issue as Readonly<Record<string, unknown>>;
    const path = typeof record["path"] === "string" ? record["path"] : "";
    const message = typeof record["message"] === "string" ? record["message"] : "";
    if (path !== "" && !(path in map)) map[path] = message;
  }
  return map;
}

/**
 * The sanitized activation reason from a `503 model_activation_failed`
 * envelope (#124), or `undefined` for anything else.
 *
 * Narrow on purpose. That response means the save PERSISTED while the saved
 * provider/model generation was refused, so the top-level message ("the
 * previous generation is still serving runs") is the same for every cause and
 * the only operator-actionable text is `details.reason` — the provider, model
 * or secret NAME to fix. It is read only for that exact status/code pair, so a
 * `details.reason` on any other envelope (a catalog-mirror 503, say) cannot be
 * rendered as an activation failure, and a blank or non-string reason is
 * treated as absent rather than shown as an empty line.
 */
export function activationFailureReason(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  if (error.status !== 503 || error.code !== "model_activation_failed") return undefined;
  const details = error.details;
  if (typeof details !== "object" || details === null || Array.isArray(details)) return undefined;
  const reason = (details as Readonly<Record<string, unknown>>)["reason"];
  if (typeof reason !== "string" || reason.trim() === "") return undefined;
  return reason;
}

/** The stored revision from a 409 `revision_conflict` envelope (CAS rebase). */
export function conflictRevision(error: unknown): number | undefined {
  if (!(error instanceof ApiError) || error.status !== 409) return undefined;
  const details = error.details;
  if (typeof details !== "object" || details === null || Array.isArray(details)) return undefined;
  const record = details as Readonly<Record<string, unknown>>;
  const revision = record["currentRevision"] ?? record["revision"];
  return typeof revision === "number" ? revision : undefined;
}
